// src/slices/claimDossier/thunk.ts
// Async thunks cho Thẩm định Hồ sơ Trả thưởng — toast xử lý NGAY TRONG THUNK, theo đúng pattern đã ổn định
// ở prizeStructure/drawResult (xem implementation-context-fe.md mục 3.4).

import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import {
  getClaimDossiersApi,
  getClaimDossierByIdApi,
  createClaimDossierApi,
  approveClaimDossierApi,
  cancelClaimDossierApi,
  payClaimDossierApi,
  appraiseClaimDossierApi,
  postAuditClaimDossierApi,
  getAgentPayoutInfoApi,
} from 'helpers/fakebackend_helper';
import { CreateClaimDossierPayload } from 'pages/ClaimDossier/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('claim_dossier_messages');

// api_helper.ts::APIClient.get() nối thẳng `key=${params[key]}` vào query string kể cả khi value là
// undefined (thành chuỗi "Status=undefined" literal) — phải lọc bỏ key null/undefined/'' trước khi gọi API,
// theo đúng pattern cleanParams() đã dùng ở slices/sinhBoSoQr, ticketType, ticketDesignTemplate.
const cleanParams = (params: any): any => {
  const cleaned: any = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

// FR-001/FR-002: danh sách hồ sơ (phân trang, lọc theo trạng thái/CCCD/số vé) — PaymentStatus dùng cho màn Kế
// toán Chi trả lọc theo trạng thái chi (ChuaChi/DaChi).
export const fetchClaimDossiers = createAsyncThunk(
  'ClaimDossier/fetchList',
  async (
    params: {
      PageIndex?: number;
      PageSize?: number;
      Status?: string;
      PaymentStatus?: string;
      ClaimantIdNumber?: string;
      TicketNumber?: string;
      // Mục 7.2 — màn Hậu kiểm Trả thưởng: 'ChoHauKiem' | 'DaHauKiem' (BE tự giới hạn AgentPaid + đã Thẩm định Phê duyệt).
      ReviewStage?: string;
      // true = BE bỏ qua enrich tốn kém (chấm lại vé, mã kỳ, đại lý, Thẩm định/Hậu kiểm) — dùng cho màn chỉ cần cột của hồ sơ.
      Lite?: boolean;
      // 'Company' (tab Trả thưởng trực tiếp) | 'Agent' (tab Thẩm định trả thưởng).
      Source?: string;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await getClaimDossiersApi(cleanParams(params));
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || st('khong_the_tai_danh_sach_ho_text');
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// FR-007: xem chi tiết 1 hồ sơ
export const fetchClaimDossierById = createAsyncThunk(
  'ClaimDossier/fetchById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getClaimDossierByIdApi(id);
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || st('khong_the_tai_chi_tiet_ho_text');
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Bổ sung — thông tin trả thưởng đại lý của 1 hồ sơ do đại lý tự lập (nút "Trả thưởng đại lý" ở màn
// Thẩm định/Hậu kiểm). Chỉ áp dụng hồ sơ có agentId; backend trả 400 nếu gọi cho hồ sơ nhân viên nội bộ lập.
export const fetchAgentPayoutInfo = createAsyncThunk(
  'ClaimDossier/fetchAgentPayoutInfo',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getAgentPayoutInfoApi(id);
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || st('khong_the_tai_thong_tin_tra_text');
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// FR-003/FR-004: tạo hồ sơ mới — server tự chấm vé + tự tính Thuế TNCN
export const createClaimDossier = createAsyncThunk(
  'ClaimDossier/create',
  async (data: CreateClaimDossierPayload, { rejectWithValue }) => {
    try {
      const response = await createClaimDossierApi(data);
      toast.success(st('tao_ho_so_thanh_cong_dang_message'));
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || st('khong_the_tao_ho_so_text');
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// FR-005: ký duyệt hồ sơ
export const approveClaimDossier = createAsyncThunk(
  'ClaimDossier/approve',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await approveClaimDossierApi(id);
      toast.success(st('ky_duyet_ho_so_thanh_cong_message'));
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || st('khong_the_ky_duyet_ho_so_text');
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// FR-006: hủy hồ sơ
export const cancelClaimDossier = createAsyncThunk(
  'ClaimDossier/cancel',
  async ({ id, reason }: { id: number; reason?: string }, { rejectWithValue }) => {
    try {
      const response = await cancelClaimDossierApi(id, reason);
      toast.success(st('da_huy_ho_so_message'));
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || st('khong_the_huy_ho_so_text');
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// README.md muc 9 — "Tham dinh" ho so do dai ly tu tao (AgentPaid), buoc 1/2.
export const appraiseClaimDossier = createAsyncThunk(
  'ClaimDossier/appraise',
  async ({ id, decision, comment }: { id: number; decision: string; comment?: string }, { rejectWithValue }) => {
    try {
      const response = await appraiseClaimDossierApi(id, decision, comment);
      toast.success(st('da_tham_dinh_ho_so_message'));
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || st('khong_the_tham_dinh_ho_so_text');
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// "Hau kiem", buoc 2/2 (chi sau khi Tham dinh Phe duyet).
export const postAuditClaimDossier = createAsyncThunk(
  'ClaimDossier/postAudit',
  async ({ id, decision, comment }: { id: number; decision: string; comment?: string }, { rejectWithValue }) => {
    try {
      const response = await postAuditClaimDossierApi(id, decision, comment);
      // Mục 7.3 — hậu kiểm từ chối thì BE tự ghi bút toán đảo điều chỉnh giảm công nợ trả thưởng đã tính cho đại lý.
      toast.success(
        decision === 'TuChoi'
          ? st('da_hau_kiem_tu_choi_cong_message')
          : st('da_hau_kiem_ho_so_message')
      );
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || st('khong_the_hau_kiem_ho_so_text');
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Màn Kế toán Chi trả — thao tác "Chuyển khoản": ChuaChi -> DaChi
export const payClaimDossier = createAsyncThunk(
  'ClaimDossier/pay',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await payClaimDossierApi(id);
      toast.success(st('da_chuyen_khoan_chi_tra_message'));
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || st('khong_the_chuyen_khoan_chi_tra_text');
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);
